import type { Params } from '@angular/router';
import type { BsModalRef } from 'ngx-bootstrap/modal';
import { BsModalService } from 'ngx-bootstrap/modal';
import type { WidgetErrorItem } from '@common/types/widget-errors.type';
import type { SiteEntity } from '@common/types/site.type';
import type { SitePageEntity } from '@common/types/site-page.type';
import type { Domain } from '@common/types/domain.type';

import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, withLatestFrom } from 'rxjs/operators';

import { EContentSources, EPageStatuses, ESiteStatuses } from '@common/enums';

import { SaveConfirmModalComponent } from '@web/shared/modals/save-confirm-modal/save-confirm-modal.component';
import { CreateNewWebsiteModalComponent } from '@web/shared/modals/create-new-website-modal/create-new-website-modal.component';
import { CreateNewPageModalComponent } from '@web/shared/modals/create-new-page-modal/create-new-page-modal.component';
import { CopyPageModalComponent } from '@web/layout/drawers/page-drawer/modals/copy-page-modal/copy-page-modal.component';
import { CopyToPageModalComponent } from '@web/shared/modals/copy-to-page-modal/copy-to-page-modal.component';
import { ConfirmDeleteUserBlockModalComponent } from '@web/shared/modals/confirm-delete-user-block';

import { SiteService } from '@web/store/website/site/site.service';

import { AppFacade } from '@web/store/app/app.facade';
import { WidgetsErrorsFacade } from '@web/store/page-structure/widgets-errors/widgets-errors.facade';
import { LibraryBlockFacade } from '@web/store/library-blocks/library-blocks.facade';
import { RouterFacade } from '@web/store/router/router.facade';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { PagesFacade } from '@web/store/website/pages/pages.facade';
import { DomainsFacade } from '@web/store/website/domains/domains.facade';
import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { ModalsFacade } from './modals.facade';

import ModalsActions from './modals.actions';
import { SettingsFacade } from '@web/store/website/settings/settings.facade';

@Injectable()
export class ModalsEffects {
    private readonly actions$ = inject(Actions);
    private readonly modalService = inject(BsModalService);
    private readonly appFacade = inject(AppFacade);
    private readonly blocksFacade = inject(BlocksFacade);
    private readonly modalsFacade = inject(ModalsFacade);
    private readonly widgetsErrorsFacade = inject(WidgetsErrorsFacade);
    private readonly routerFacade = inject(RouterFacade);
    private readonly siteFacade = inject(SiteFacade);
    private readonly pagesFacade = inject(PagesFacade);
    private readonly domainsFacade = inject(DomainsFacade);
    private readonly siteService = inject(SiteService);
    private readonly libraryBlocksFacade = inject(LibraryBlockFacade);
    private readonly settingsFacade = inject(SettingsFacade);

    public readonly showSaveConfirmModal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ModalsActions.showSaveConfirmModal),
                map(({ exit, publish, navigateTo }) => {
                    return { exit, publish, navigateTo };
                }),
                withLatestFrom(
                    this.widgetsErrorsFacade.widgetErrorsAsArray$,
                    ({ exit, publish, navigateTo }, errors: WidgetErrorItem[]) => ({
                        exit,
                        publish,
                        navigateTo,
                        errors,
                    })
                ),
                map(({ exit, publish, navigateTo, errors }) => {
                    if (!errors?.length) {
                        return this.modalsFacade.showSaveModalHandler(exit, publish, navigateTo);
                    }

                    const modal: BsModalRef<SaveConfirmModalComponent> = this.modalService.show(
                        SaveConfirmModalComponent,
                        {
                            initialState: { exit, errors },
                        }
                    );
                    modal.content.onClose.subscribe((result: boolean) => {
                        if (result) {
                            this.modalsFacade.showSaveModalHandler(exit, publish, navigateTo);
                        }
                    });
                })
            ),
        { dispatch: false }
    );

    public readonly showSaveModalHandler$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ModalsActions.showSaveModalHandler),
                map(({ exit, publish, navigateTo }) => ({ exit, publish, navigateTo })),
                withLatestFrom(
                    this.routerFacade.routerParams$,
                    this.appFacade.isAdmin$,
                    this.siteFacade.site$,
                    this.pagesFacade.pages$,
                    this.pagesFacade.isEduSite$,
                    this.settingsFacade.siteMainPageId$,
                    (
                        { exit, publish, navigateTo },
                        params: Params,
                        isAdmin: boolean,
                        site: SiteEntity,
                        pages: SitePageEntity[],
                        isEduSite: boolean,
                        mainPageId: string
                    ) => ({
                        exit,
                        publish,
                        navigateTo,
                        params,
                        isAdmin,
                        site,
                        pages,
                        isEduSite,
                        mainPageId,
                    })
                ),
                map(({ exit, publish, navigateTo, params, isAdmin, site, pages, isEduSite, mainPageId }) => {
                    const isIndexPage = pages?.length
                        ? pages[0]?.id.toString() === params.pageId || params.pageId === mainPageId
                        : true;

                    if (
                        (this.siteService.isNewSite(params, isAdmin) || site.status === ESiteStatuses.New) &&
                        isIndexPage &&
                        !isEduSite
                    ) {
                        return this.modalsFacade.showSaveSiteModal(exit, publish, navigateTo);
                    }

                    const currentPage = pages.find((page) => page.id.toString() === params.pageId);
                    const isEduPage = [EContentSources.Education, EContentSources.WebEducation].includes(
                        currentPage.content_source_id
                    );
                    if (
                        (this.siteService.isNewPage(params, isAdmin) || currentPage?.status === EPageStatuses.New) &&
                        !isIndexPage &&
                        !isEduPage
                    ) {
                        return this.modalsFacade.showSavePageModal(exit, publish, navigateTo);
                    }

                    return this.siteFacade.saveSiteRequest(exit, publish, params.pageId, navigateTo);
                })
            ),
        { dispatch: false }
    );

    public readonly showSaveSiteModal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ModalsActions.showSaveSiteModal),
                map(({ exit, publish, navigateTo }) => ({ exit, publish, navigateTo })),
                withLatestFrom(
                    this.siteFacade.name$,
                    this.domainsFacade.siteDomain$,
                    this.routerFacade.routerParams$,
                    ({ exit, publish, navigateTo }, name: string, domain: Domain, routerParams: Params) => ({
                        exit,
                        publish,
                        navigateTo,
                        name,
                        domain,
                        routerParams,
                    })
                ),
                map(({ exit, publish, navigateTo, name, domain, routerParams }) => {
                    this.modalService.show(CreateNewWebsiteModalComponent, {
                        initialState: {
                            exit,
                            publish,
                            navigateTo,
                            initialName: name,
                            initialDomain: domain,
                            siteId: routerParams.siteId,
                        },
                    });
                })
            ),
        { dispatch: false }
    );

    public readonly showSavePageModal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ModalsActions.showSavePageModal),
                withLatestFrom(
                    this.pagesFacade.pages$,
                    this.routerFacade.routerParams$,
                    ({ exit, publish, navigateTo }, pages: SitePageEntity[], params: Params) => ({
                        exit,
                        publish,
                        navigateTo,
                        pages,
                        params,
                    })
                ),
                map(({ exit, publish, navigateTo, pages, params }) => {
                    const currentPage = pages.find((page) => page.id.toString() === params.pageId);
                    const url = currentPage?.url ? new URL(currentPage.url) : null;
                    this.modalService.show(CreateNewPageModalComponent, {
                        initialState: {
                            exit,
                            publish,
                            navigateTo,
                            initialName: currentPage.name,
                            initialPageUrl: url ? (url.pathname === '/' ? '' : url.pathname.replace('/', '')) : '',
                            siteId: params.siteId,
                            pageId: params.pageId,
                        },
                    });
                })
            ),
        { dispatch: false }
    );

    public readonly showCopyPageModal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ModalsActions.showCopyPageModal),
                withLatestFrom(this.siteFacade.site$, ({ siteId, page }, site) => ({ siteId, page, site })),
                map(({ page, siteId, site }) => {
                    this.modalService.show(CopyPageModalComponent, {
                        initialState: {
                            siteId,
                            page,
                            siteDomain: site.url,
                        },
                    });
                })
            ),
        { dispatch: false }
    );

    public readonly showCopyToPageModal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ModalsActions.showCopyBlockToPageModal),
                map(({ block, currentSiteId, currentPageId }) => {
                    const modalRef: BsModalRef<CopyToPageModalComponent> = this.modalService.show(
                        CopyToPageModalComponent,
                        {
                            initialState: {
                                block,
                                currentSiteId,
                                currentPageId,
                            },
                            ignoreBackdropClick: true,
                        }
                    );

                    modalRef.content.onClose.subscribe((result) => {
                        if (typeof result === 'boolean' && result === false) return;

                        this.blocksFacade.copyBlockToPageRequest(result, block);
                    });
                })
            ),
        { dispatch: false }
    );

    public readonly confirmDeleteUserLibraryBlock$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(ModalsActions.showConfirmDeleteUserLibraryBlockModal),
                map(({ blockId }) => {
                    const modalRef: BsModalRef<ConfirmDeleteUserBlockModalComponent> = this.modalService.show(
                        ConfirmDeleteUserBlockModalComponent
                    );

                    modalRef.content.onClose.subscribe((result) => {
                        if (!result) {
                            return;
                        }

                        this.libraryBlocksFacade.markLibraryBlockAsDeleted(blockId.toString());
                    });
                })
            ),
        { dispatch: false }
    );
}
